/**
 * Why a sign-in did not finish, in words.
 *
 * SHARED, because the callback redirects to `/` — the front page — and the
 * only place that read `login_error` was the console's sign-in screen, which
 * is not where anybody lands. So a failed login dropped somebody on the front
 * page, signed out, with the reason sitting unread in the address bar. That
 * was reported as "I don\'t know why I can\'t do anything".
 *
 * The reasons come from `failLogin` in services/api/src/routes/auth.ts. An
 * unknown one is not a crash: this is the LAST thing between a person and
 * giving up, so it always says something, and something that suggests an
 * action.
 */
export const LOGIN_ERRORS: Record<string, string> = {
  cancelled: 'You cancelled the Twitch sign-in.',
  expired: 'That sign-in link had already been used or had expired. Try again.',
  refused: 'Twitch would not complete the sign-in. Try again.',
  incomplete: 'Twitch sent us back without the details we needed. Try again.',
  // Connecting a channel shares the callback, so its refusals land here too.
  connect_mismatch:
    'Twitch was signed in as a different account from the one you use on tomo, so ' +
    'nothing was connected. Sign in to twitch.tv as the channel you want to connect first. Try again.',
  connect_slug_taken:
    'Another tomo channel already uses that Twitch name. Contact tomo support to sort it out.',
  connect_scopes_missing:
    'Twitch did not grant everything a channel needs, so nothing was connected. Approve every permission when Twitch asks. Try again.',
  moderate_mismatch:
    'Twitch was signed in as a different account from the one you use on tomo, so nothing was ' +
    'changed. Sign in to twitch.tv as yourself first. Try again.',
  moderate_scopes_missing:
    'Twitch did not grant everything mod tools need, so nothing was changed. Approve every permission when Twitch asks. Try again.',
};

export function loginErrorMessage(reason: string | null): string | null {
  if (reason === null || reason.length === 0) return null;
  return LOGIN_ERRORS[reason] ?? 'That sign-in did not complete. Try again.';
}
